import { createSlice } from "@reduxjs/toolkit";
import sortBy from "lodash/sortBy";

import i18n from "../i18n.js";

const STATES = [
  "requested",
  "request_accepted",
  "request_declined",
  "contacted",
  "replied",
  "screened",
  "declined",
  "hired",
];

const PROMOTED_LANGS = ["en", "fr", "de", "it", "pt", "ru", "es"];

function createFilters(state, filters) {
  const lastSelected = Object.values(state.filters)
    .flat()
    .filter((filter) => filter.selected);
  return {
    areaOfWork: filters.areaOfWork.map((key) => ({
      description: i18n.t(`talent_filters.area_of_work.${key}.description`),
      text: i18n.t(`talent_filters.area_of_work.${key}.title`),
      value: key,
      type: "areaOfWork",
      selected: lastSelected.some(
        (aow) => aow.type === "areaOfWork" && aow.value === key
      ),
    })),

    regions: filters.regions.map((region) => ({
      text: region.name,
      value: region.id,
      promoted: region.promoted,
      type: "regions",
      selected: lastSelected.some(
        (aow) => aow.type === "regions" && aow.value === region.id
      ),
    })),
    workRole: filters.workRole.map((role) => ({
      text: role,
      value: role,
      type: "workRole",
      selected: lastSelected.some(
        (aow) => aow.type === "workRole" && aow.value === role
      ),
    })),
    company: filters.company
      .filter((x) => x.company)
      .sort((a, b) => a.company.localeCompare(b.company))
      .map(({ company, promoted }) => ({
        text: company,
        value: company,
        promoted,
        type: "company",
        selected: lastSelected.some(
          (aow) => aow.type === "company" && aow.value === company
        ),
      })),
    language: filters.languages
      .filter((x) => x)
      .map((lang) => lang.toLowerCase())
      .map((lang) => ({
        text: i18n.t(`languages.${lang}`),
        value: lang,
        type: "language",
        promoted: PROMOTED_LANGS.includes(lang),
        selected: lastSelected.some(
          (aow) => aow.type === "language" && aow.value === lang
        ),
      }))
      .sort((a, b) => a.text.localeCompare(b.text)),
    skills: filters.skills
      .filter((x) => x)
      .sort((a, b) => a.localeCompare(b))
      .map((skill) => ({
        text: skill,
        value: skill,
        type: "skills",
        selected: lastSelected.some(
          (aow) => aow.type === "skills" && aow.value === skill
        ),
      })),
    state: filters.state.map((state) => ({
      text: i18n.t(`component.talent_state.${state}`),
      value: state,
      type: "state",
      selected: lastSelected.some(
        (aow) => aow.type === "state" && aow.value === state
      ),
    })),
    yearsOfExperience: filters.yearsOfExperience.map((years) => ({
      description: "",
      text: i18n.t(`pages.talent_pool.years_of_experience`, {
        count: years,
      }),
      value: years,
      type: "yearsOfExperience",
      selected: lastSelected.some(
        (aow) => aow.type === "yearsOfExperience" && aow.value === years
      ),
    })),
  };
}

export const talentPoolSlice = createSlice({
  name: "talentPool",
  initialState: {
    lastSeen: "talents",
    filters: {},
    filtersLoaded: false,
    filtersLoading: false,
    filtersLoadingError: null,
    items: {},
    itemsLoaded: false,
    itemsLoading: false,
    itemsLoadingError: null,
    itemsMeta: {},
    page: {},
    previewMode: true,
    features: {},
    favoritesChanged: false,
  },
  reducers: {
    setLastSeen: (state, { payload }) => {
      state.lastSeen = payload;
    },
    setCurrentPage: (state, { payload: { id, page } }) => {
      state.page = { ...state.page, [id]: page };
    },
    setTalents: (state, { payload: { id, filters, items, meta } }) => {
      state.items = { ...state.items, [id]: items };
      state.itemsMeta = { ...state.itemsMeta, [id]: meta };
    },
    setTalentFilters: (state, { payload: filters }) => {
      state.filters = createFilters(state, filters);
    },
    addStageFilterIfNeeded: (state, { payload }) => {
      if (!state.filters.state.find((state) => state.value === payload)) {
        state.filters.state = sortBy(
          [
            ...state.filters.state,
            {
              text: i18n.t(`component.talent_state.${payload}`),
              value: payload,
              type: "state",
              selected: false,
            },
          ],
          (s) => STATES.indexOf(s.value)
        );
      }
    },
    toggleTalentFilter: (state, { payload: { changedItem, id } }) => {
      state.page = { ...state.page, [id]: 1 };
      state.filters = {
        ...state.filters,
        [changedItem.type]: state.filters[changedItem.type].map((filter) =>
          filter.value === changedItem.value
            ? { ...filter, selected: !filter.selected }
            : filter
        ),
      };
    },
    updateTalentStateState: (state, { payload }) => {
      state.items = Object.keys(state.items).reduce(
        (memo, key) => ({
          ...memo,
          [key]: state.items[key].map((item) =>
            item.id === payload.talentId
              ? {
                  ...item,
                  state: payload.state,
                  stateRequestedBy: payload.stateRequestedBy,
                }
              : item
          ),
        }),
        {}
      );
    },
    updateTalentFavoriteState: (state, { payload }) => {
      state.items = Object.keys(state.items).reduce(
        (memo, key) => ({
          ...memo,
          [key]: state.items[key].map((item) =>
            item.id === payload.talentId
              ? { ...item, favorite: payload.favorite }
              : item
          ),
        }),
        {}
      );
    },
    setFavoritesChanged: (state, { payload }) => {
      state.favoritesChanged = payload;
    },
    setFeatures: (state, { payload }) => {
      state.features = payload;
    },
    setPreviewMode: (state, { payload }) => {
      state.previewMode = payload;
    },
  },
});

// SELECTORS

export const selectCurrentFilters = (state) =>
  Object.values(state.talentPool.filters)
    .flat()
    .filter((filter) => filter.selected);

export const selectPreviewMode = (state) => state.talentPool.previewMode;
export const selectCanSeeTalentPool = (state) =>
  state.talentPool.features.talentPool;

export const {
  addStageFilterIfNeeded,
  setCurrentPage,
  setLastSeen,
  setTalents,
  setTalentFilters,
  toggleTalentFilter,
  updateTalentStateState,
  updateTalentFavoriteState,
  setFavoritesChanged,
  setFeatures,
  setPreviewMode,
} = talentPoolSlice.actions;

export default talentPoolSlice.reducer;
